import {
  FeatureFlagsDto,
  PlatformType,
  type Booth,
  type BoothStatusData,
  type WebRegionResolveResponse,
} from '@cibli/shared';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { LocationBridge } from '@/bridge/location.bridge';
import { useNetworkMonitor } from '@/features/settings/contexts/network-monitor-context';
import { useSystem } from '@/features/settings/hooks/useSystem';
import { upsertBooth } from '@/services/booths';
import { featureFlagsService } from '@/services/featureFlags';
import { resolveWebRegion } from '@/services/webRegion';

import { useBoothControlSocket } from '../hooks/useBoothControlSocket';
import { usePrinter } from '../hooks/usePrinter';
import { BoothContext, type BoothContextType, type UseBoothError } from './booth-context';

const extendedBoothPlatformTypes = [PlatformType.Booth, PlatformType.Dedicated];

type BoothProviderProps = {
  children: ReactNode;
};

export function BoothProvider({ children }: BoothProviderProps) {
  const printFunctions = usePrinter();
  const { boothStatus, isPrintMode } = useSystem();
  const { online, quality, lastFullTest } = useNetworkMonitor();

  const canUpsertBooth = useMemo(() => {
    if (isPrintMode || !boothStatus.system?.deviceId) {
      return false;
    }
    return true;
  }, [isPrintMode, boothStatus]);

  const defaultLoading = useMemo(() => {
    return boothStatus.loading || !canUpsertBooth;
  }, [boothStatus.loading, canUpsertBooth]);

  const [loading, setLoading] = useState<boolean>(defaultLoading);
  const [error, setError] = useState<UseBoothError | null>(null);
  const [booth, setBooth] = useState<Booth | Partial<Booth> | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagsDto | null>(null);
  // Incrémenté quand le BO pousse un changement de réglages (relecture forcée)

  // Listen to remote control commands (speedtest, etc.) via WebSocket
  useBoothControlSocket(booth);

  // Build network payload: merge existing interfaces with live quality metrics
  const buildNetworkPayload = useCallback(
    () => ({
      interfaces: boothStatus.network?.interfaces ?? [],
      online,
      status: quality.status,
      ...(quality.latencyAvgMs != null ? { latencyMs: quality.latencyAvgMs } : {}),
      ...(quality.jitterMs != null ? { jitterMs: quality.jitterMs } : {}),
      lossPercent: quality.lossPercent,
      ...(lastFullTest?.downloadMbps != null ? { downloadMbps: lastFullTest.downloadMbps } : {}),
      ...(lastFullTest?.uploadMbps != null ? { uploadMbps: lastFullTest.uploadMbps } : {}),
      ...(lastFullTest?.testedAt ? { testedAt: lastFullTest.testedAt.toISOString() } : {}),
    }),
    [
      boothStatus.network?.interfaces,
      online,
      quality.status,
      quality.latencyAvgMs,
      quality.jitterMs,
      quality.lossPercent,
      lastFullTest?.downloadMbps,
      lastFullTest?.uploadMbps,
      lastFullTest?.testedAt,
    ],
  );

  // GPS position: sent with each upsert so the backend can geocode the real
  // device location. Returns undefined when unavailable (permission denied...).
  const buildPayload = useCallback(async () => {
    const location = await LocationBridge.getPosition();
    return {
      ...boothStatus,
      network: buildNetworkPayload(),
      ...(location ? { location } : {}),
    } as unknown as BoothStatusData;
  }, [boothStatus, buildNetworkPayload]);

  useEffect(() => {
    (async () => {
      if (!canUpsertBooth) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const result = await upsertBooth({
          machineUid: boothStatus.system?.deviceId,
          data: await buildPayload(),
        });
        setBooth(result);
      } catch (e) {
        console.warn('Failed to upsert booth configuration for machine', e);
        setError('lookup_failed');
        setBooth(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [canUpsertBooth, buildPayload, boothStatus.system?.deviceId]);

  const [webRegionConfig, setWebRegionConfig] = useState<WebRegionResolveResponse | null>(null);

  // Resolve web region when running on the web (no physical machine deviceId)
  useEffect(() => {
    if (canUpsertBooth) return;

    (async () => {
      try {
        const config = await resolveWebRegion();
        setWebRegionConfig(config);
        Sentry.setTag('country', config.countryCode);
        Sentry.setTag('channel', 'web');
      } catch (err) {
        console.warn('Failed to resolve web region:', err);
      }
    })();
  }, [canUpsertBooth]);

  useEffect(() => {
    if (booth?.id != null) {
      Sentry.setTag('boothId', String(booth.id));
    }
  }, [booth?.id]);

  const platformType = useMemo(() => {
    if (booth?.platformType) {
      return booth.platformType as PlatformType;
    }
    // Do not prematurely fallback to Web while system info or booth upsert is still resolving
    if (boothStatus.loading || loading) {
      return null;
    }
    if (!canUpsertBooth) {
      return PlatformType.Web;
    }
    return null;
  }, [booth, canUpsertBooth, boothStatus.loading, loading]);

  const isWeb = useMemo(() => {
    return platformType === PlatformType.Web;
  }, [platformType]);

  useEffect(() => {
    if (platformType != null) {
      Sentry.setTag('platformType', platformType);
    }
  }, [platformType]);

  const loadFeatureFlags = useCallback(async () => {
    try {
      const boothId = typeof booth?.id === 'string' ? booth.id : undefined;
      const flags = await featureFlagsService.getFeatureFlags(boothId);
      setFeatureFlags(flags);
    } catch (err) {
      console.error('Failed to load feature flags:', err);
    }
  }, [booth?.id]);

  // Un réglage de la cabine vient d'être modifié côté Back-Office. Flow
  // produit (2026-10-22) : la validation de la locale de base dans le BO
  // force la RECHARGE DE L'APPLICATION sur l'appareil ciblé — la nouvelle
  // locale s'applique dès l'écran d'accueil. Le backend ne notifie cet
  // évènement que pour les changements de locale (defaultLocaleId, etc.).
  const forceReload = useCallback(() => {
    window.location.reload();
  }, []);

  // Listen to remote control commands (speedtest, etc.) and real-time flags update via WebSocket
  useBoothControlSocket(booth, {
    onFlagsUpdated: loadFeatureFlags,
    onConfigUpdated: forceReload,
  });

  useEffect(() => {
    loadFeatureFlags();
  }, [loadFeatureFlags]);

  // Re-fetch feature flags whenever notified by backend via WebSocket (/booth-control),
  // with a relaxed fallback interval (5 minutes instead of 30 seconds) to prevent network noise.
  useEffect(() => {
    const FALLBACK_INTERVAL_MS = 5 * 60_000;
    const interval = setInterval(() => {
      loadFeatureFlags();
    }, FALLBACK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadFeatureFlags]);

  // Heartbeat: upsert every 2 minutes to keep booth alive
  useEffect(() => {
    if (!canUpsertBooth) return;
    const HEARTBEAT_INTERVAL_MS = 2 * 60_000;
    const interval = setInterval(() => {
      void (async () => {
        try {
          await upsertBooth({
            machineUid: boothStatus.system?.deviceId,
            data: await buildPayload(),
          });
        } catch {
          // Heartbeat failure is non-fatal
        }
      })();
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [canUpsertBooth, boothStatus.system?.deviceId, buildPayload]);

  const isBooth = useMemo(() => {
    if (!platformType) {
      return false;
    }
    return extendedBoothPlatformTypes.includes(platformType);
  }, [platformType]);

  const isDevNoInterviewEnabled = useMemo(() => {
    return featureFlagsService.isDevNoInterviewActivated(featureFlags);
  }, [featureFlags]);

  const isSimulationModeEnabled = useMemo(() => {
    return featureFlagsService.isSimulationModeActivated(featureFlags);
  }, [featureFlags]);

  const isKioskDashboardButtonEnabled = useMemo(() => {
    return featureFlagsService.isKioskDashboardButtonActivated(featureFlags);
  }, [featureFlags]);

  const isDevOtpBypassEnabled = useMemo(() => {
    return featureFlagsService.isDevOtpBypassActivated(featureFlags);
  }, [featureFlags]);

  const isMaintenanceModeEnabled = useMemo(() => {
    return featureFlagsService.isMaintenanceModeActivated(featureFlags);
  }, [featureFlags]);

  const isSecretPinEnabled = useMemo(() => {
    return featureFlagsService.isSecretPinActivated(featureFlags);
  }, [featureFlags]);

  const isCvThemesEnabled = useMemo(() => {
    return featureFlagsService.isCvThemesActivated(featureFlags);
  }, [featureFlags]);

  const isBoothDefaultLocaleEnabled = useMemo(() => {
    return featureFlagsService.isBoothDefaultLocaleActivated(featureFlags);
  }, [featureFlags]);

  const value: BoothContextType = {
    status: boothStatus as BoothStatusData,
    booth,
    loading,
    error,
    isBooth,
    isWeb,
    platformType,
    webRegionConfig,
    featureFlags,
    isDevNoInterviewEnabled,
    isSimulationModeEnabled,
    isKioskDashboardButtonEnabled,
    isDevOtpBypassEnabled,
    isMaintenanceModeEnabled,
    isSecretPinEnabled,
    isCvThemesEnabled,
    isBoothDefaultLocaleEnabled,
    loadFeatureFlags,
    ...printFunctions,
    printerAvailable: boothStatus?.printing?.printerAvailable ?? false,
  };

  return <BoothContext.Provider value={value}>{children}</BoothContext.Provider>;
}
